// Payment / paywall screen for Iron Forums.
//
// Members land here after onboarding and on every login until their dues
// subscription is confirmed server-side. The screen handles the full lifecycle:
// entry, redirect to Stripe-hosted Checkout, "processing" while we wait for the
// webhook (poll-confirm), success (clears the paywall), and declined/canceled
// with a clear retry. When Stripe isn't configured it shows a calm
// "not configured" state instead of crashing — and never fakes a payment.

import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { paymentsAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { isNativePlatform } from '../native';
import type { MembershipLevel, PaymentStatusResponse } from '../types';
import {
  resolvePaymentScreen,
  type PaymentScreen as Screen,
} from '../lib/paymentGate';
import { SUPPORT_EMAIL } from '../utils/support';
import {
  Loader2,
  Lock,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  LogOut,
  RefreshCw,
  CreditCard,
} from 'lucide-react';

const MEMBERSHIP_LABELS: Record<Exclude<MembershipLevel, ''>, { label: string; price: string }> = {
  monthly: { label: 'Monthly Membership', price: '$400 / month' },
  annual: { label: 'Annual Membership', price: '$4,800 / year' },
  stakeholder: { label: 'Stakeholder', price: '$500 / month' },
  scholarship: { label: 'Scholarship', price: 'By facilitator approval' },
};

// How long to keep polling for webhook confirmation after returning from Stripe.
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 16; // ~40s

export default function Payment() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();

  const [screen, setScreen] = useState<Screen>('loading');
  const [info, setInfo] = useState<PaymentStatusResponse | null>(null);
  const [error, setError] = useState('');
  const [coverFee, setCoverFee] = useState(true);
  const pollRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const returnStatus = searchParams.get('status');
  const sessionId = searchParams.get('session_id') ?? undefined;

  const finish = useCallback(async () => {
    try {
      await refreshUser();
    } catch (refreshError) {
      console.error('Payment confirmed, but user refresh failed:', refreshError);
    } finally {
      navigate('/', { replace: true });
    }
  }, [navigate, refreshUser]);

  // Poll server state until Stripe's signed webhook confirms activation.
  const pollUntilConfirmed = useCallback(async () => {
    if (!mountedRef.current) return;
    pollRef.current += 1;
    try {
      const data = await paymentsAPI.getStatus(sessionId);
      if (!mountedRef.current) return;
      setInfo(data);
      if (!data.requires_payment) {
        setScreen('success');
        await finish();
        return;
      }
      if (data.payment_status === 'failed' || data.payment_status === 'past_due') {
        setScreen('failed');
        return;
      }
    } catch {
      // ignore transient errors; keep polling
    }
    if (pollRef.current >= MAX_POLLS) {
      setScreen('pending_slow');
      return;
    }
    if (mountedRef.current) {
      timerRef.current = window.setTimeout(pollUntilConfirmed, POLL_INTERVAL_MS);
    }
  }, [finish, sessionId]);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      try {
        const data = await paymentsAPI.getStatus(returnStatus === 'success' ? sessionId : undefined);
        if (cancelled) return;
        setInfo(data);

        // Already cleared (paid, scholarship, exempt, or Stripe off) — leave.
        if (data.configured && !data.requires_payment && returnStatus !== 'success') {
          await finish();
          return;
        }

        // Native apps never run checkout: show a neutral blocked screen with
        // no purchase links (store-rule compliant), and release automatically
        // once the server says dues are settled.
        if (isNativePlatform) {
          if (!data.requires_payment) {
            await finish();
            return;
          }
          setScreen('native_blocked');
          return;
        }

        if (returnStatus === 'success') {
          if (!data.requires_payment) {
            setScreen('success');
            await finish();
          } else {
            setScreen('processing');
            pollRef.current = 0;
            timerRef.current = window.setTimeout(pollUntilConfirmed, POLL_INTERVAL_MS);
          }
          return;
        }
        if (returnStatus === 'cancel') {
          setScreen('canceled');
          return;
        }
        const nextScreen = resolvePaymentScreen(data);
        setScreen(nextScreen);
        if (nextScreen === 'processing') {
          pollRef.current = 0;
          timerRef.current = window.setTimeout(
            pollUntilConfirmed,
            POLL_INTERVAL_MS,
          );
        }
      } catch {
        if (!cancelled) {
          setScreen('idle');
          setError('Could not load your payment status. Please try again.');
        }
      }
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Native "Check again": one status refresh, no polling and no Stripe copy.
  const recheckNative = async () => {
    setScreen('loading');
    try {
      const data = await paymentsAPI.getStatus();
      if (!mountedRef.current) return;
      setInfo(data);
      if (!data.requires_payment) {
        setScreen('success');
        await finish();
        return;
      }
    } catch {
      // Show the blocked screen again; the member can retry.
    }
    if (mountedRef.current) setScreen('native_blocked');
  };

  const startCheckout = async () => {
    setError('');
    setScreen('redirecting');
    try {
      const res = await paymentsAPI.createCheckoutSession(coverFee);
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      if (res.configured === false) {
        setScreen('not_configured');
        return;
      }
      setError(res.error || 'Could not start checkout.');
      setScreen('idle');
    } catch (checkoutError) {
      if (
        axios.isAxiosError(checkoutError)
        && checkoutError.response?.data?.reason === 'payment_not_required'
      ) {
        await finish();
        return;
      }
      const message = axios.isAxiosError(checkoutError)
        ? checkoutError.response?.data?.error
        : null;
      setError(message || 'Could not start checkout. Please try again.');
      setScreen('idle');
    }
  };

  // Prefer the member's invite-tied forum + per-forum price; fall back to the
  // legacy per-tier label for older tier-only members.
  const tier = info?.forum_name
    ? { label: info.forum_name, price: info.forum_price ? `${info.forum_price}` : '' }
    : info?.membership_level
    ? MEMBERSHIP_LABELS[info.membership_level]
    : null;

  return (
    <div className="standalone-screen flex flex-col items-center p-4">
      {/* mt-auto/mb-auto center the card + escape hatches as a group, while
          staying reachable by scroll when the viewport is too short. */}
      <div className="w-full max-w-md mt-auto bg-white rounded-2xl shadow-xl p-8">
        <Header screen={screen} />

        <Body
          screen={screen}
          tier={tier}
          firstName={user?.first_name}
          error={error}
          info={info}
          coverFee={coverFee}
          onCoverFeeChange={setCoverFee}
          onCheckout={startCheckout}
          onFinish={finish}
          onRetry={startCheckout}
          onRecheck={() => {
            if (isNativePlatform) {
              void recheckNative();
              return;
            }
            setScreen('processing');
            pollRef.current = 0;
            void pollUntilConfirmed();
          }}
        />
      </div>

      {/* Escape hatches so a member is never trapped behind the paywall. */}
      <div className="mt-6 mb-auto flex items-center gap-4 text-sm text-gray-500">
        {user?.payment_lapsed && (
          <>
            <button
              onClick={() => navigate('/')}
              className="hover:text-gray-700 transition"
            >
              Back to the app
            </button>
            <span className="text-gray-300">·</span>
          </>
        )}
        <button
          onClick={async () => {
            await logout();
            navigate('/login', { replace: true });
          }}
          className="inline-flex items-center gap-1.5 hover:text-gray-700 transition"
        >
          <LogOut size={14} />
          Log out
        </button>
        <span className="text-gray-300">·</span>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="hover:text-gray-700 transition"
        >
          Contact support
        </a>
        <span className="text-gray-300">·</span>
        <Link to="/terms" className="hover:text-gray-700 transition">Terms</Link>
      </div>
    </div>
  );
}

function Header({ screen }: { screen: Screen }) {
  const isSuccess = screen === 'success';
  const title = isSuccess
    ? "You're all set"
    : screen === 'native_blocked'
    ? 'Membership not active'
    : 'Activate your membership';
  return (
    <div className="text-center mb-6">
      <div
        className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
          isSuccess ? 'bg-green-100' : 'bg-navy/10'
        }`}
      >
        {isSuccess ? (
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        ) : (
          <Lock className="w-8 h-8 text-navy" />
        )}
      </div>
      <h1 className="text-xl font-bold text-navy">{title}</h1>
    </div>
  );
}

interface BodyProps {
  screen: Screen;
  tier: { label: string; price: string } | null;
  firstName?: string;
  error: string;
  info: PaymentStatusResponse | null;
  coverFee: boolean;
  onCoverFeeChange: (next: boolean) => void;
  onCheckout: () => void;
  onFinish: () => void;
  onRetry: () => void;
  onRecheck: () => void;
}

function FeeChoice({
  info,
  coverFee,
  onCoverFeeChange,
}: {
  info: PaymentStatusResponse | null;
  coverFee: boolean;
  onCoverFeeChange: (next: boolean) => void;
}) {
  if (!info?.processing_fee_price) return null;
  const total = coverFee ? info.total_with_fee_price : info.base_monthly_price;
  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3 space-y-2">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={coverFee}
          onChange={(event) => onCoverFeeChange(event.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-navy focus:ring-gold"
        />
        <span className="text-sm text-gray-700">
          Add {info.processing_fee_percent}% to cover card processing
          {info.processing_fee_price ? ` (${info.processing_fee_price})` : ''}.
          This keeps the full dues amount with Iron Forums.
        </span>
      </label>
      {total && (
        <p className="text-sm font-semibold text-navy pl-7">
          Total billed: {total}
        </p>
      )}
    </div>
  );
}

function Body({ screen, tier, firstName, error, info, coverFee, onCoverFeeChange, onCheckout, onFinish, onRetry, onRecheck }: BodyProps) {
  const canRestartCheckout = !isNativePlatform;
  switch (screen) {
    case 'loading':
      return (
        <div className="py-8 space-y-6 text-center">
          <div className="flex justify-center">
            <Loader2 className="w-7 h-7 text-navy animate-spin" />
          </div>
          {canRestartCheckout && (
            <button
              onClick={onRetry}
              className="text-sm text-gray-400 hover:text-navy transition"
            >
              Taking too long? Start payment over
            </button>
          )}
        </div>
      );

    case 'not_configured':
      return (
        <Notice
          icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
          tone="amber"
          title="Payments aren't set up yet"
          body="Online dues payment is temporarily unavailable. Contact your facilitator or support so they can correct the setup or waive the dues before you continue."
        />
      );

    case 'no_tier':
      return (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Your Forum has not assigned dues to this membership yet. Contact
            your facilitator or support so they can finish the setup.
          </p>
        </div>
      );

    case 'no_price':
      return (
        <Notice
          icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
          tone="amber"
          title="This tier isn't billable online yet"
          body="Your Forum-assigned dues are not configured for online payment. Please contact your facilitator or support to finish activating your membership."
        />
      );

    case 'idle':
    case 'redirecting':
      return (
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            {firstName ? `${firstName}, you're` : "You're"} one step from full
            access. Your Forum assigned the dues shown below. They are billed
            as a recurring subscription under your membership agreement.
          </p>
          {tier && (
            <div className="flex items-center justify-between rounded-xl border border-navy/20 bg-navy/5 px-4 py-3">
              <div>
                <p className="font-semibold text-navy">{tier.label}</p>
                <p className="text-sm text-gray-500">{tier.price}</p>
              </div>
              <CreditCard className="w-5 h-5 text-navy" />
            </div>
          )}
          <FeeChoice info={info} coverFee={coverFee} onCoverFeeChange={onCoverFeeChange} />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            onClick={onCheckout}
            disabled={screen === 'redirecting'}
            className="w-full flex items-center justify-center gap-2 bg-gold text-white py-3 rounded-lg font-semibold hover:bg-gold-dark transition disabled:opacity-60"
          >
            {screen === 'redirecting' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecting to secure checkout…
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Continue to secure payment
              </>
            )}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
            <ShieldCheck size={13} />
            Payments are processed securely by Stripe. We never see your card details.
          </p>
        </div>
      );

    case 'processing':
      return (
        <div className="space-y-4 text-center">
          <div className="py-4 flex justify-center">
            <Loader2 className="w-8 h-8 text-navy animate-spin" />
          </div>
          <p className="text-sm text-gray-600">
            Confirming your payment with Stripe… this usually takes just a few
            seconds. Please don't close this window.
          </p>
          {canRestartCheckout && (
            <button
              onClick={onRetry}
              className="text-sm text-gray-400 hover:text-navy transition"
            >
              Stuck? Start over with a fresh checkout
            </button>
          )}
        </div>
      );

    case 'resume':
      return (
        <div className="space-y-5">
          <Notice
            icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
            tone="amber"
            title="Checkout wasn't completed"
            body="It looks like you started checkout but didn't finish — no charge was made. You can pick up right where you left off."
          />
          {tier && (
            <div className="flex items-center justify-between rounded-xl border border-navy/20 bg-navy/5 px-4 py-3">
              <div>
                <p className="font-semibold text-navy">{tier.label}</p>
                <p className="text-sm text-gray-500">{tier.price}</p>
              </div>
              <CreditCard className="w-5 h-5 text-navy" />
            </div>
          )}
          <FeeChoice info={info} coverFee={coverFee} onCoverFeeChange={onCoverFeeChange} />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 bg-gold text-white py-3 rounded-lg font-semibold hover:bg-gold-dark transition"
          >
            <Lock className="w-4 h-4" />
            Continue to secure payment
          </button>
          <button
            onClick={onRecheck}
            className="w-full text-navy py-2 rounded-lg font-semibold hover:bg-navy/5 transition"
          >
            I already paid — check my status
          </button>
        </div>
      );

    case 'native_blocked':
      return (
        <div className="space-y-5">
          <Notice
            icon={<Lock className="w-5 h-5 text-navy" />}
            tone="navy"
            title="Your membership isn't active yet"
            body="To finish setting up your membership, sign in at app.ironforums.org in any web browser. Once it's active, this screen will unlock automatically. Your facilitator or our support team can help if you get stuck."
          />
          <button
            onClick={onRecheck}
            className="w-full flex items-center justify-center gap-2 bg-navy text-white py-3 rounded-lg font-semibold hover:bg-navy-dark transition"
          >
            <RefreshCw size={16} />
            Check again
          </button>
        </div>
      );

    case 'pending_slow':
      return (
        <div className="space-y-4">
          <Notice
            icon={<Loader2 className="w-5 h-5 text-navy animate-spin" />}
            tone="navy"
            title="Still confirming"
            body="Your payment is taking a little longer than usual to confirm. This can happen briefly — your access will unlock automatically once it clears."
          />
          <button
            onClick={onRecheck}
            className="w-full flex items-center justify-center gap-2 bg-navy text-white py-3 rounded-lg font-semibold hover:bg-navy-dark transition"
          >
            <RefreshCw size={16} />
            Check again
          </button>
          <button
            onClick={onRetry}
            className="w-full text-navy py-2 rounded-lg font-semibold hover:bg-navy/5 transition"
          >
            Try again with a fresh checkout
          </button>
        </div>
      );

    case 'success':
      return (
        <div className="space-y-5 text-center">
          <p className="text-sm text-gray-600">
            Your membership is active. Welcome to Iron Forums — let's get started.
          </p>
          <button
            onClick={onFinish}
            className="w-full bg-navy text-white py-3 rounded-lg font-semibold hover:bg-navy-dark transition"
          >
            Enter Iron Forums
          </button>
        </div>
      );

    case 'canceled':
      return (
        <div className="space-y-5">
          <Notice
            icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
            tone="amber"
            title="Payment canceled"
            body="No charge was made. You can try again whenever you're ready."
          />
          <button
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 bg-gold text-white py-3 rounded-lg font-semibold hover:bg-gold-dark transition"
          >
            <RefreshCw size={16} />
            Try again
          </button>
        </div>
      );

    case 'failed':
      return (
        <div className="space-y-5">
          <Notice
            icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
            tone="red"
            title="Payment didn't go through"
            body="Your card was declined or the payment failed. Please try again with a different payment method."
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 bg-gold text-white py-3 rounded-lg font-semibold hover:bg-gold-dark transition"
          >
            <RefreshCw size={16} />
            Try again
          </button>
        </div>
      );

    default:
      return null;
  }
}

function Notice({
  icon,
  title,
  body,
  tone,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  tone: 'amber' | 'red' | 'navy';
}) {
  const toneClass = {
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50',
    navy: 'border-navy/20 bg-navy/5',
  }[tone];
  return (
    <div className={`rounded-xl border ${toneClass} p-4`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">{icon}</span>
        <div>
          <p className="text-sm font-semibold text-navy">{title}</p>
          <p className="text-sm text-gray-600 mt-1">{body}</p>
        </div>
      </div>
    </div>
  );
}
